import {MeshCellSettingsProps} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {FocusedArea} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/WorkbenchState';

export enum KeyActions
{
    // Spreadsheet
    MoveActiveCellLeft = 'moveActiveCellLeft',
    MoveActiveCellUp = 'moveActiveCellUp',
    MoveActiveCellRight = 'moveActiveCellRight',
    MoveActiveCellDown = 'moveActiveCellDown',
    StartEditingCell = 'startEditingCell',
    StartEditingOrCreateNote = 'startEditingOrCreateNote',
    CreateNamedRanges = 'createNamedRanges',
    ToggleNamedRangesVisibility = 'toggleNamedRangesVisibility',
    StopBindingOrStartEditingOrMoveActiveCell = 'stopBindingOrStartEditingOrMoveActiveCell',
    EnterNestedFrameViewOrMoveActiveCellUp = 'enterNestedFrameView',
    ExitDeepestNestedFrameView = 'exitDeepestNestedFrameView',
    ClearSelectedCells = 'clearSelectedCells',
    ClearSelectedCellValues = 'clearSelectedCellValues',
    ToggleSelectedCellsLaziness = 'toggleSelectedCellsLaziness',
    ExtendSelectionLeft = 'extendSelectionLeft',
    ExtendSelectionUp = 'extendSelectionUp',
    ExtendSelectionRight = 'extendSelectionRight',
    ExtendSelectionDown = 'extendSelectionDown',
    ChangeCellFontToBold = 'changeCellFontToBold',
    MaskCellValue = 'maskCellValue',
    ToggleTextWrappingClip = 'toggleTextWrappingClip',
    SelectAll = 'selectAll',
    ExecuteLazyCell = 'executeLazyCell',
    StopCellEvaluation = 'stopCellEvaluation',
    Reset = 'reset',
    MoveActiveCellToImportantLeft = 'moveActiveCellToImportantLeft',
    MoveActiveCellToImportantUp = 'moveActiveCellToImportantUp',
    MoveActiveCellToImportantRight = 'moveActiveCellToImportantRight',
    MoveActiveCellToImportantDown = 'moveActiveCellToImportantDown',
    ExtendSelectionToImportantLeft = 'extendSelectionToImportantLeft',
    ExtendSelectionToImportantUp = 'extendSelectionToImportantUp',
    ExtendSelectionToImportantRight = 'extendSelectionToImportantRight',
    ExtendSelectionToImportantDown = 'extendSelectionToImportantDown',
    Copy = 'copy',
    Cut = 'cut',
    Mode = 'mode',
    PasteValuesOnly = 'pasteAsPlainText'
}

export class MeshKeyboardController
{
    public static readonly KEY_MAP = {
        // Spreadsheet
        [KeyActions.MoveActiveCellLeft]: ['left', 'shift+tab'],
        [KeyActions.MoveActiveCellUp]: 'up',
        [KeyActions.MoveActiveCellRight]: ['right', 'tab'],
        [KeyActions.MoveActiveCellDown]: 'down',
        [KeyActions.StartEditingCell]: 'f2',
        [KeyActions.StartEditingOrCreateNote]: 'shift+f2',
        [KeyActions.ToggleNamedRangesVisibility]: 'f3',
        [KeyActions.CreateNamedRanges]: 'shift+f3',
        [KeyActions.StopBindingOrStartEditingOrMoveActiveCell]: 'enter',
        [KeyActions.EnterNestedFrameViewOrMoveActiveCellUp]: 'shift+enter',
        [KeyActions.ExitDeepestNestedFrameView]: 'shift+esc',
        [KeyActions.ClearSelectedCells]: ['del', 'backspace'],
        [KeyActions.ClearSelectedCellValues]: ['mod+del', 'mod+backspace'],
        [KeyActions.ToggleSelectedCellsLaziness]: 'mod+/',
        [KeyActions.ExtendSelectionLeft]: 'shift+left',
        [KeyActions.ExtendSelectionUp]: 'shift+up',
        [KeyActions.ExtendSelectionRight]: 'shift+right',
        [KeyActions.ExtendSelectionDown]: 'shift+down',
        [KeyActions.ChangeCellFontToBold]: 'mod+b',
        [KeyActions.MaskCellValue]: ['mod+shift+8', 'mod+*'],
        [KeyActions.ToggleTextWrappingClip]: ['mod+.'],
        [KeyActions.SelectAll]: 'mod+a',
        [KeyActions.ExecuteLazyCell]: 'mod+enter',
        [KeyActions.StopCellEvaluation]: ['mod+esc'],
        [KeyActions.Reset]: 'esc',
        [KeyActions.MoveActiveCellToImportantLeft]: 'mod+left',
        [KeyActions.MoveActiveCellToImportantUp]: 'mod+up',
        [KeyActions.MoveActiveCellToImportantRight]: 'mod+right',
        [KeyActions.MoveActiveCellToImportantDown]: 'mod+down',
        [KeyActions.ExtendSelectionToImportantLeft]: 'mod+shift+left',
        [KeyActions.ExtendSelectionToImportantUp]: 'mod+shift+up',
        [KeyActions.ExtendSelectionToImportantRight]: 'mod+shift+right',
        [KeyActions.ExtendSelectionToImportantDown]: 'mod+shift+down',
        [KeyActions.Copy]: 'mod+c',
        [KeyActions.Cut]: 'mod+x',
        [KeyActions.Mode]: 'mod',
        [KeyActions.PasteValuesOnly]: 'mod+shift+v'
    };

    public getKeyMap():any
    {
        return MeshKeyboardController.KEY_MAP;
    }

    public handleMeshFocus = () =>
    {
        const {workbench} = controllers.ui;

        workbench.setFocusedArea(FocusedArea.Spreadsheet);
    };

    public getKeyHandlers(ctx:IWSPMeshContext):any
    {
        const {isEditMode} = controllers.ui.spreadsheets.main;

        return {
            [KeyActions.MoveActiveCellLeft]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellLeft(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.MoveActiveCellUp]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellUp(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.MoveActiveCellRight]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellRight(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.MoveActiveCellDown]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellDown(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.StartEditingCell]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode && !meshState.details.isReadonly )
                {
                    const sheet:AnySheet = controllers.ui.spreadsheets.main.findAnySheetById(ctx.sheetId, true);
                    const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(sheet.workbook.id);
                    const activeCell = meshState.details.activeCell;
                    const formula = controllers.ui.spreadsheets.sheet.getSlotFormula(ctx.sheetId, activeCell.toSlotLocation());

                    if( Type.isInstanceOf(formula, Type.Frame) )
                    {
                        controllers.ui.spreadsheets.frameDetails.enterNestedView(
                            sheet.workbook.id,
                            activeSheet.id,
                            ctx.sheetId,
                            activeCell.toSlotLocation()
                        );

                        event.stopPropagation();
                        event.preventDefault();
                    }
                    else
                    {
                        controllers.ui.spreadsheets.mesh.startEditingCell(ctx);

                        event.stopPropagation();
                        event.preventDefault();
                    }
                }
            },
            [KeyActions.StartEditingOrCreateNote]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !meshState.details.isReadonly )
                {
                    controllers.ui.spreadsheets.mesh.startEditingOrCreateNote(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ToggleNamedRangesVisibility]: (event:KeyboardEvent) =>
            {
                const {main} = controllers.ui.spreadsheets;
                main.toggleNamedRangesVisible(ctx);
                event.stopPropagation();
                event.preventDefault();
            },
            [KeyActions.CreateNamedRanges]: (event:KeyboardEvent) =>
            {
                const {main} = controllers.ui.spreadsheets;
                main.createNewNamedRange(ctx);
                event.stopPropagation();
                event.preventDefault();
            },
            [KeyActions.StopBindingOrStartEditingOrMoveActiveCell]: (event:KeyboardEvent) =>
            {
                const {bindings} = controllers.ui;

                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( bindings.isBindingInProgress() )
                {
                    bindings.stopBinding();

                    event.stopPropagation();
                    event.preventDefault();
                }
                else
                {
                    const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                    if( meshState.details.selectedRange && !meshState.details.selectedRange.isSingleCell )
                    {
                        controllers.ui.spreadsheets.meshSelection.moveActiveCellDown(ctx, true);

                        event.stopPropagation();
                        event.preventDefault();
                    }
                    else if( !isEditMode && !meshState.details.isReadonly )
                    {
                        const sheet:AnySheet = controllers.ui.spreadsheets.main.findAnySheetById(ctx.sheetId, true);
                        const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(sheet.workbook.id);
                        const activeCell = meshState.details.activeCell;
                        const formula = controllers.ui.spreadsheets.sheet.getSlotFormula(ctx.sheetId, activeCell.toSlotLocation());

                        if( Type.isInstanceOf(formula, Type.Frame) )
                        {
                            controllers.ui.spreadsheets.frameDetails.enterNestedView(
                                sheet.workbook.id,
                                activeSheet.id,
                                ctx.sheetId,
                                activeCell.toSlotLocation()
                            );

                            event.stopPropagation();
                            event.preventDefault();
                        }
                        else
                        {
                            controllers.ui.spreadsheets.mesh.startEditingCell(ctx);

                            event.stopPropagation();
                            event.preventDefault();
                        }
                    }
                }
            },
            [KeyActions.EnterNestedFrameViewOrMoveActiveCellUp]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
                const activeCell = meshState.details.activeCell;

                const details = meshState.details;

                if( meshState.details.isEditNoteMode ) return;

                if( details.selectedRange && !details.selectedRange.isSingleCell )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellUp(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
                else if( activeCell )
                {
                    const sheet:AnySheet = controllers.ui.spreadsheets.main.findAnySheetById(ctx.sheetId, true);
                    const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(sheet.workbook.id);

                    controllers.ui.spreadsheets.frameDetails.enterNestedView(
                        sheet.workbook.id,
                        activeSheet.id,
                        ctx.sheetId,
                        activeCell.toSlotLocation()
                    );

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExitDeepestNestedFrameView]: (event:KeyboardEvent) =>
            {
                const sheet:AnySheet = controllers.ui.spreadsheets.main.findAnySheetById(ctx.sheetId, true);
                const activeSheet = controllers.ui.spreadsheets.tabs.getActiveSheet(sheet.workbook.id);

                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                controllers.ui.spreadsheets.frameDetails.exitDeepestNestedView(sheet.workbook.id, activeSheet.id);
            },
            [KeyActions.ClearSelectedCells]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode && !meshState.details.isReadonly )
                {
                    controllers.ui.spreadsheets.mesh.clearSelectedCells(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ClearSelectedCellValues]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode && !meshState.details.isReadonly )
                {
                    controllers.ui.spreadsheets.mesh.clearSelectedCellValues(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ToggleSelectedCellsLaziness]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode && !meshState.details.isReadonly )
                {
                    controllers.ui.spreadsheets.mesh.toggleSelectedCellsLaziness(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionLeft]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionLeft(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionUp]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionUp(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionRight]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionRight(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionDown]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionDown(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ChangeCellFontToBold]: (event:KeyboardEvent) =>
            {
                controllers.ui.spreadsheets.meshCellSettings.toggleProperty(ctx, MeshCellSettingsProps.TextBold);

                event.stopPropagation();
                event.preventDefault();
            },
            [KeyActions.MaskCellValue]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
                const details = meshState.details;

                if( meshState.details.isEditNoteMode ) return;

                if( !details.isReadonly )
                {
                    controllers.ui.spreadsheets.meshCellSettings.toggleProperty(ctx, MeshCellSettingsProps.IsValueMasked);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ToggleTextWrappingClip]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
                const details = meshState.details;

                if( details.isEditNoteMode ) return;

                controllers.ui.spreadsheets.meshCellSettings.toggleTextWrappingClip(ctx);

                event.stopPropagation();
                event.preventDefault();
            },
            [KeyActions.SelectAll]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.selectAll(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExecuteLazyCell]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode && !meshState.details.isReadonly )
                {
                    controllers.ui.spreadsheets.mesh.evaluateSelectedCells(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.StopCellEvaluation]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode && !meshState.details.isReadonly )
                {
                    controllers.ui.spreadsheets.mesh.abortSelectedCellsEvaluation(ctx);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.Reset]: (event:KeyboardEvent) =>
            {
                const {meshCopyPaste} = controllers.ui.spreadsheets;
                const {bindings} = controllers.ui;

                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( bindings.isBindingInProgress() )
                {
                    bindings.stopBinding();

                    event.stopPropagation();
                    event.preventDefault();
                }
                else if( meshCopyPaste.hasCopiedData(ctx.sheetId) )
                {
                    meshCopyPaste.resetCopiedData(ctx.sheetId);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.MoveActiveCellToImportantLeft]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellLeft(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.MoveActiveCellToImportantUp]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellUp(ctx, false, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.MoveActiveCellToImportantRight]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellRight(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.MoveActiveCellToImportantDown]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.moveActiveCellDown(ctx, false, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionToImportantLeft]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionLeft(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionToImportantUp]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionUp(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionToImportantRight]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionRight(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.ExtendSelectionToImportantDown]: (event:KeyboardEvent) =>
            {
                const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

                if( meshState.details.isEditNoteMode ) return;

                if( !isEditMode )
                {
                    controllers.ui.spreadsheets.meshSelection.extendSelectionDown(ctx, true);

                    event.stopPropagation();
                    event.preventDefault();
                }
            },
            [KeyActions.Copy]: (event:KeyboardEvent) =>
            {
                const {mesh} = controllers.ui.spreadsheets;
                const meshState = mesh.getState(ctx);

                if( isEditMode || meshState.details.isEditNoteMode )
                    return;

                event.stopPropagation();
                event.preventDefault();

                controllers.ui.spreadsheets.meshCopyPaste.copySlotToClipboard(
                    ctx.sheetId,
                    false,
                    ctx.isNested
                );
            },
            [KeyActions.Cut]: (event:KeyboardEvent) =>
            {
                const {mesh} = controllers.ui.spreadsheets;
                const meshState = mesh.getState(ctx);

                if( isEditMode || meshState.details.isEditNoteMode )
                    return;

                event.stopPropagation();
                event.preventDefault();

                controllers.ui.spreadsheets.meshCopyPaste.copySlotToClipboard(
                    ctx.sheetId,
                    true,
                    ctx.isNested
                );
            },
            [KeyActions.Mode]: () =>
            {
                const {mesh} = controllers.ui.spreadsheets;
                const meshState = mesh.getState(ctx);

                if( !isEditMode && !meshState.details.isEditNoteMode )
                    controllers.ui.spreadsheets.mesh.focusMesh(ctx);
            },
            [KeyActions.PasteValuesOnly]: (event:KeyboardEvent) =>
            {
                controllers.ui.spreadsheets.meshCopyPaste.pasteValuesOnly(ctx.sheetId);

                event.stopPropagation();
                event.preventDefault();
            }
        };
    }
}
